import { useEffect, useRef } from 'react';
import { useQueries } from '@tanstack/react-query';
import { fetchJSON } from './api-client';
import type { NewsItem } from '@invest-copilot/shared';

interface NewsResp {
  items: NewsItem[];
}

/** 一批最多預熱幾檔：壓住 /api/news 並發 + batch analyze prompt 大小。 */
const MAX_PREWARM = 20;

/**
 * 新聞 AI 摘要「批次預熱」：把自選 + 進場機會候選的新聞，一次送 /api/analyze（批次模式），
 * 先算好寫進後端 Cosmos cache。之後使用者點任一檔，NewsCard 的單檔 analyze 直接命中後端
 * cache（零 LLM）→ 點來點去不再重複消耗 quota。
 *
 * 設計重點：
 * - 各檔新聞用「與 NewsCard 單檔完全相同的 queryKey / 參數」抓 → 共享 react-query 快取，
 *   且後端 cache key（newsFingerprint）同源 → 單檔點到時一定命中。
 * - batch POST 以「symbol + 新聞 id 指紋」去重，避免同一批重複送、避免每次 render 重打。
 * - 任何失敗都 swallow，不影響頁面（快慢分層：預熱罷工不擋 UI）。
 */
export function useNewsPrewarm(opts: {
  symbols: string[];
  source: string;
  lang: string;
  region: string;
  enabled: boolean;
}): void {
  const { source, lang, region, enabled } = opts;
  const symbols = opts.symbols.slice(0, MAX_PREWARM);

  // 與 App 單檔 news query 同 queryKey → 共享快取、後端 cache 同源
  const newsQueries = useQueries({
    queries: (enabled ? symbols : []).map((sym) => ({
      queryKey: ['news', sym, source, lang, region],
      queryFn: () =>
        fetchJSON<NewsResp>(
          `/api/news/${encodeURIComponent(sym)}?count=12&source=${source}&lang=${lang}&region=${region}`,
        ),
      staleTime: 5 * 60_000,
      gcTime: 30 * 60_000,
      retry: false,
    })),
  });

  const prewarmed = useRef<Set<string>>(new Set());

  // readiness 指紋：哪些 symbol 的新聞已到齊（含各自新聞數）；到齊狀態變了才重評估送批次
  const readiness = enabled
    ? symbols.map((sym, i) => `${sym}#${newsQueries[i]?.data?.items?.length ?? 0}`).join(',')
    : '';

  useEffect(() => {
    if (!enabled) return;

    const fp = (symbol: string, news: NewsItem[]) =>
      `${symbol}:${news.map((n) => n.id).sort().join('|')}`;

    const pending: { symbol: string; news: NewsItem[] }[] = [];
    symbols.forEach((sym, i) => {
      const items = newsQueries[i]?.data?.items;
      if (!items || items.length === 0) return;
      if (prewarmed.current.has(fp(sym, items))) return;
      pending.push({ symbol: sym, news: items });
    });
    if (pending.length === 0) return;

    // 樂觀標記，避免 effect 連跑重複送；失敗再移除讓下次重試
    for (const p of pending) prewarmed.current.add(fp(p.symbol, p.news));

    fetchJSON('/api/analyze', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: pending }),
    }).catch(() => {
      for (const p of pending) prewarmed.current.delete(fp(p.symbol, p.news));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, readiness, source, lang, region]);
}
